import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Users, Search, Pencil, GitMerge, ShieldCheck, History } from "lucide-react";
import { toast } from "sonner";

export default function Visitors() {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [profile, setProfile] = useState(null);
  const [mergeFrom, setMergeFrom] = useState(null);
  const [mergeTarget, setMergeTarget] = useState("");
  const [saving, setSaving] = useState(false);
  const canManage = hasPerm("visitors.manage");

  const load = useCallback(() => {
    api.get(`/visitors${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(({ data }) => setRows(data || [])).catch(() => {});
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openProfile = async (v) => {
    try {
      const { data } = await api.get(`/visitors/${v.id}`);
      setProfile(data);
    } catch { toast.error("Failed to load profile"); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/visitors/${editing.id}`, {
        name: editing.name, email: editing.email, phone: editing.phone,
        gender: editing.gender || "", preferences: editing.preferences, notes: editing.notes,
      });
      toast.success("Visitor updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const doMerge = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/visitors/merge", { primary_id: mergeTarget, duplicate_id: mergeFrom.id });
      toast.success(`Merged — ${data.bookings_moved} booking(s) moved`);
      setMergeFrom(null); setMergeTarget("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="visitors-page">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope, sans-serif" }}>
          <Users className="h-8 w-8 text-[#1B4332]" /> Visitors
        </h1>
        <p className="text-[#525C58] text-sm mt-1">Every guest gets a unique ID — preferences, history and license info for fast repeat bookings.</p>
      </div>
      <div className="relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A8A84]" />
        <Input className="pl-9" placeholder="Search name, email or V-number…" value={q}
               onChange={(e) => setQ(e.target.value)} data-testid="visitor-search" />
      </div>
      <Card className="border-[#E8E8E6]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead>
                <TableHead>Gender</TableHead><TableHead>Stays</TableHead><TableHead>Last stay</TableHead>
                <TableHead>Preferences</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((v) => (
                <TableRow key={v.id} data-testid={`visitor-row-${v.id}`}>
                  <TableCell><Badge className="bg-[#1B4332]/10 text-[#1B4332] border-0 font-mono text-[10px]">{v.visitor_no}</Badge></TableCell>
                  <TableCell className="font-medium">
                    <button onClick={() => openProfile(v)} className="hover:underline text-left" data-testid={`visitor-name-${v.id}`}>{v.name}</button>
                    {(v.aliases || []).length > 0 && <span className="block text-[10px] text-[#7A8A84]">aka {v.aliases.join(", ")}</span>}
                  </TableCell>
                  <TableCell className="text-xs text-[#525C58]">{v.email || "—"}</TableCell>
                  <TableCell className="text-xs">{v.gender || "—"}</TableCell>
                  <TableCell className="text-xs">{v.booking_count}</TableCell>
                  <TableCell className="text-xs">{v.last_stay_to || "—"}</TableCell>
                  <TableCell className="text-xs max-w-[200px] truncate" title={v.preferences}>{v.preferences || "—"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {canManage && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing({ ...v })} data-testid={`edit-visitor-${v.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-amber-700" title="Merge into another visitor"
                                onClick={() => { setMergeFrom(v); setMergeTarget(""); }} data-testid={`merge-visitor-${v.id}`}>
                          <GitMerge className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-[#7A8A84] py-8">No visitors found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="visitor-edit-dialog">
          <DialogHeader><DialogTitle>Edit visitor — {editing?.visitor_no}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="visitor-edit-name" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1"><Label>Email</Label>
                  <Input value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
                <div className="space-y-1"><Label>Phone</Label>
                  <Input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label>Gender</Label>
                <select className="w-full h-9 rounded-md border border-[#E8E8E6] px-2 text-sm" value={editing.gender || ""}
                        onChange={(e) => setEditing({ ...editing, gender: e.target.value })} data-testid="visitor-edit-gender">
                  <option value="">Not specified</option><option value="male">Male</option><option value="female">Female</option>
                </select></div>
              <div className="space-y-1"><Label>Preferences</Label>
                <Textarea rows={2} value={editing.preferences || ""} onChange={(e) => setEditing({ ...editing, preferences: e.target.value })}
                          placeholder="Room, dietary, transport preferences…" data-testid="visitor-edit-preferences" /></div>
              <div className="space-y-1"><Label>Notes</Label>
                <Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="visitor-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge dialog */}
      <Dialog open={!!mergeFrom} onOpenChange={(o) => !o && setMergeFrom(null)}>
        <DialogContent className="max-w-md" data-testid="visitor-merge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitMerge className="h-5 w-5 text-amber-700" /> Merge duplicate</DialogTitle>
            <DialogDescription>
              All bookings of <strong>{mergeFrom?.name} ({mergeFrom?.visitor_no})</strong> will move to the visitor you pick below.
              The duplicate is archived (kept as an alias). This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label>Merge into</Label>
            <select className="w-full h-9 rounded-md border border-[#E8E8E6] px-2 text-sm" value={mergeTarget}
                    onChange={(e) => setMergeTarget(e.target.value)} data-testid="merge-target-select">
              <option value="">Choose the visitor to keep…</option>
              {rows.filter((r) => r.id !== mergeFrom?.id).map((r) => (
                <option key={r.id} value={r.id}>{r.visitor_no} — {r.name} ({r.booking_count} stays)</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeFrom(null)}>Cancel</Button>
            <Button onClick={doMerge} disabled={!mergeTarget || saving} className="bg-amber-600 hover:bg-amber-700" data-testid="merge-confirm-btn">
              {saving ? "Merging…" : "Merge records"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile dialog */}
      <Dialog open={!!profile} onOpenChange={(o) => !o && setProfile(null)}>
        <DialogContent className="max-w-lg" data-testid="visitor-profile-dialog">
          <DialogHeader>
            <DialogTitle>{profile?.name} <Badge className="ml-2 bg-[#1B4332]/10 text-[#1B4332] border-0 font-mono text-[10px]">{profile?.visitor_no}</Badge></DialogTitle>
            <DialogDescription>{profile?.email} {profile?.phone && `· ${profile.phone}`} {profile?.gender && `· ${profile.gender}`}</DialogDescription>
          </DialogHeader>
          {profile?.preferences && <p className="text-sm bg-[#FAFAF8] border border-[#E8E8E6] rounded p-2"><strong>Preferences:</strong> {profile.preferences}</p>}
          {profile?.license_summary && (
            <div className="text-sm bg-emerald-50/60 border border-emerald-200 rounded p-2" data-testid="license-summary">
              <p className="font-medium flex items-center gap-1 text-emerald-900"><ShieldCheck className="h-4 w-4" /> Last medical license</p>
              <p className="text-xs text-emerald-800 mt-0.5">
                Status: {profile.license_summary.last_status || "—"} · Profession: {profile.license_summary.profession || "—"}
                {profile.license_summary.license_number && ` · #${profile.license_summary.license_number}`}
              </p>
            </div>
          )}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#525C58] flex items-center gap-1 mb-1">
              <History className="h-3.5 w-3.5" /> Stay history ({(profile?.bookings || []).length})
            </p>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {(profile?.bookings || []).map((b) => (
                <div key={b.id} className="flex items-center gap-2 text-xs border-b border-[#F1F1EE] py-1">
                  <span>{b.stay_from} → {b.stay_to}</span>
                  <Badge className="text-[9px] bg-[#F1F1EE] text-[#525C58] border-0">{b.status}</Badge>
                  {b.needs_medical_license && <Badge className="text-[9px] bg-emerald-50 text-emerald-800 border border-emerald-200">license: {b.medical_license_status || "requested"}</Badge>}
                  {(b.comments || []).length > 0 && <span className="text-[#7A8A84]">{b.comments.length} comment(s)</span>}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
